import { trialEndInstructionBody } from 'pages/tpm/constants';

export const NOTIFICATION_SEVERITY = {
    ERROR: 'error',
    WARNING: 'warning',
};

export const NOTIFICATION_DESTINATION = {
    NUMBERS: 'numbers',
    USERS: 'users',
    ADMINS: 'admins',
    API_KEYS: 'apiKeys',
    // The Services dashboard itself: the warning scrolls to Service status.
    SERVICES: 'services',
    // Starts the Microsoft sync upgrade instead of going anywhere.
    UPGRADE_SYNC: 'upgradeSync',
};

export const NOTIFICATION_KEYS = {
    COMPANY_NOT_APPROVED: 'companyNotApproved',
    TRIAL_EXPIRED: 'trialExpired',
    SERVICE_CANNOT_CONNECT: 'serviceCannotConnect',
    ACCOUNT_DEACTIVATED: 'accountDeactivated',
    LEGACY_SYNC_RETIREMENT: 'legacySyncRetirement',
    NUMBERS_ACTION_REQUIRED: 'numbersActionRequired',
    INACTIVE_ADMINS: 'inactiveAdmins',
    API_KEYS_EXPIRING: 'apiKeysExpiring',
    APP_AUTH_FAILURE: 'appAuthFailure',
    SERVICE_ATTENTION: 'serviceAttention',
    USERS_IN_ERROR: 'usersInError',
    DEGRADED_TRUNKS: 'degradedTrunks',
    TRS_REGISTRATION_FAILURE: 'trsRegistrationFailure',
};

const { ERROR, WARNING } = NOTIFICATION_SEVERITY;
const { NUMBERS, USERS, ADMINS, API_KEYS, SERVICES, UPGRADE_SYNC } =
    NOTIFICATION_DESTINATION;

/**
 * (3153) Every alert the dashboard can raise: first the account-wide ones
 * that used to be banners (2635), then the story's, in the order it lists
 * them. `label` is the warning's subject and `detail` the text under it.
 * Warnings with no `destination` have nothing to open.
 */
export const NOTIFICATION_TYPES = [
    {
        key: NOTIFICATION_KEYS.COMPANY_NOT_APPROVED,
        label: 'Company not approved',
        detail: 'Services will not provision until your company has been approved. Please contact your provider for more information.',
        severity: WARNING,
    },
    {
        key: NOTIFICATION_KEYS.TRIAL_EXPIRED,
        label: 'Trial expired',
        detail: trialEndInstructionBody.join(' '),
        severity: WARNING,
    },
    {
        key: NOTIFICATION_KEYS.SERVICE_CANNOT_CONNECT,
        label: 'Service cannot connect',
        detail: 'Please grant access. Calling may be disabled if this issue is not resolved.',
        severity: ERROR,
        destination: SERVICES,
    },
    {
        key: NOTIFICATION_KEYS.ACCOUNT_DEACTIVATED,
        label: 'Account deactivated',
        detail: 'Calling has been disabled. Please contact your provider.',
        severity: ERROR,
    },
    {
        key: NOTIFICATION_KEYS.LEGACY_SYNC_RETIREMENT,
        label: 'Legacy Microsoft authentication',
        detail: 'Upgrade to prevent service delays and avoid disruption.',
        severity: WARNING,
        destination: UPGRADE_SYNC,
    },
    {
        key: NOTIFICATION_KEYS.NUMBERS_ACTION_REQUIRED,
        label: 'Numbers need action',
        detail: 'Numbers in an action required state',
        severity: WARNING,
        destination: NUMBERS,
    },
    {
        key: NOTIFICATION_KEYS.INACTIVE_ADMINS,
        label: 'Inactive admins',
        detail: 'Admins not logged in within 90 days',
        severity: WARNING,
        destination: ADMINS,
    },
    {
        key: NOTIFICATION_KEYS.API_KEYS_EXPIRING,
        label: 'API keys expiring',
        detail: 'Expired or expiring within 30 days',
        severity: WARNING,
        destination: API_KEYS,
    },
    {
        key: NOTIFICATION_KEYS.APP_AUTH_FAILURE,
        label: 'App auth failure',
        detail: 'An application authorisation has failed',
        severity: ERROR,
        destination: SERVICES,
    },
    {
        key: NOTIFICATION_KEYS.SERVICE_ATTENTION,
        label: 'Service needs attention',
        detail: 'Service requires configuration',
        severity: WARNING,
        destination: SERVICES,
    },
    {
        key: NOTIFICATION_KEYS.USERS_IN_ERROR,
        label: 'Users in error',
        detail: 'Users in an error state',
        severity: ERROR,
        destination: USERS,
    },
    {
        key: NOTIFICATION_KEYS.DEGRADED_TRUNKS,
        label: 'Degraded trunks',
        detail: 'Trunks currently in a degraded state',
        severity: ERROR,
        destination: SERVICES,
    },
    {
        key: NOTIFICATION_KEYS.TRS_REGISTRATION_FAILURE,
        label: 'TRS registration failure',
        detail: 'TRS registration could not complete',
        severity: ERROR,
        destination: NUMBERS,
    },
];

/**
 * Where a warning takes the user. Everything else returns null: Services is
 * this page, so the warning scrolls to Service status instead of navigating.
 *
 * @returns {{ path: string, state?: object } | null}
 */
export const getNotificationRoute = (destination) => {
    switch (destination) {
        case NUMBERS:
            return { path: '/numbers' };
        case USERS:
            return { path: '/users' };
        case ADMINS:
            return { path: '/admins' };
        // API users are the second tab of the Admin Accounts page.
        case API_KEYS:
            return { path: '/admins', state: { index: 1 } };
        default:
            return null;
    }
};
